import * as z from 'zod';
import { ALLOWED_LAYOUT_PARAMS } from '@tilelist/domain/composition/BracketLayout.ts';

/*                                                                                                                                   */

/*                                   */
const MAX_SANITIZATION_ITERATIONS = 100;

/*                     */
const DANGEROUS_TAGS_PATTERN =
    /<\s*\/?\s*(?:script|iframe|object|embed|link|meta|base|form|input|button|select|textarea|style|svg|math|details|marquee)\b(?:[^>]*>)?/gi;

/*                          */
const JAVASCRIPT_PROTOCOL_PATTERN = /javascript\s*:/gi;
const VBSCRIPT_PROTOCOL_PATTERN = /vbscript\s*:/gi;
const DATA_PROTOCOL_PATTERN = /data\s*:/gi;

/*                               */
const EVENT_HANDLER_PATTERN = /\bon\w+\s*=/gi;

/*                            */
const FORMACTION_PATTERN = /formaction\s*=/gi;

/*                        */
const SRCDOC_PATTERN = /srcdoc\s*=/gi;

/*                      */
const CSS_EXPRESSION_PATTERN = /expression\s*\(/gi;

/*                    */
const CSS_BEHAVIOR_PATTERN = /behavior\s*:/gi;

/*                 */
/*                                                                               */
const NULL_BYTE_PATTERN = /\x00/g;

/*                                                 */
const MULTIPLE_SPACES_PATTERN = /\s+/g;

/*                                                                                     */
const QUOTE_TAG_ESCAPE_PATTERN = /["'`]\s*>(?=\s*<)/g;

/**
 *
 */
const urlDecode = (value: string): string => {
    let decoded = value;
    for (let i = 0; i < 3; i++) {
        try {
            const next = decodeURIComponent(decoded);
            if (next === decoded) break;
            decoded = next;
        } catch {
            break;
        }
    }
    return decoded;
};

/**
 *
 */
const removeDangerousPatterns = (value: string): string => {
    let sanitized = value;
    let previous;
    let iterations = 0;

    do {
        previous = sanitized;

        /*                         */
        sanitized = sanitized
            .replace(JAVASCRIPT_PROTOCOL_PATTERN, '')
            .replace(VBSCRIPT_PROTOCOL_PATTERN, '')
            .replace(DATA_PROTOCOL_PATTERN, '');

        /*                    */
        sanitized = sanitized.replace(EVENT_HANDLER_PATTERN, '');

        /*                    */
        sanitized = sanitized.replace(DANGEROUS_TAGS_PATTERN, '');

        /*                                */
        sanitized = sanitized
            .replace(FORMACTION_PATTERN, '')
            .replace(SRCDOC_PATTERN, '')
            .replace(CSS_EXPRESSION_PATTERN, '')
            .replace(CSS_BEHAVIOR_PATTERN, '');

        /*                     */
        sanitized = sanitized.replace(QUOTE_TAG_ESCAPE_PATTERN, '>');

        iterations++;

        if (iterations >= MAX_SANITIZATION_ITERATIONS) {
            /*                                            */
            throw new z.ZodError([
                {
                    code: 'custom',
                    message: 'Input contains excessive nested dangerous content',
                    path: [],
                },
            ]);
        }
    } while (sanitized !== previous);

    return sanitized;
};

/**
 *
 */
const applySanitization = (value: string): string => {
    const originalValue = value;

    /*         */
    let sanitized = urlDecode(value);

    /*                */
    sanitized = sanitized.replace(NULL_BYTE_PATTERN, ' ');

    /*                                                                                    */
    sanitized = removeDangerousPatterns(sanitized);

    /*                   */
    sanitized = sanitized.replace(MULTIPLE_SPACES_PATTERN, ' ');

    /*                                                                                  */
    return sanitized !== urlDecode(originalValue) ? sanitized : originalValue;
};

/**
 *
 *
 *
 */
export const ruleSchema = z
    .string()
    .max(10000)
    .min(1)
    .transform(applySanitization)
    .refine((val) => val.length > 0, { message: 'Rule cannot be empty after sanitization' });

export const offsetSchema = z.string().regex(/^\d+$/);

export const sortOrderSchema = z
    .string()
    .nonempty()
    .max(50)
    .regex(/^[a-zA-Z-]*$/);

export const layoutSchema = z.optional(z.enum(ALLOWED_LAYOUT_PARAMS));

export const compareIdSchema = z
    .string()
    .nonempty()
    .max(50)
    .regex(/^[\w.\-/äüößÄÜÖẞ]*$/);

/*                          */
export const searchTermSchema = z
    .string()
    .nonempty()
    .regex(/^[^<>"';]*$/);

/**
 *
 *
 */
export const MAX_VARIATION_IDS = 6;

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const normalizeVariationIds = (input: string | undefined | null): string[] => {
    if (!input) return [];
    const seen = new Set<string>();
    const result: string[] = [];
    for (const token of input.split(',')) {
        if (!/^[A-Za-z0-9]+$/.test(token)) continue;
        if (seen.has(token)) continue;
        seen.add(token);
        result.push(token);
        if (result.length >= MAX_VARIATION_IDS) break;
    }
    return result;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const variationIdsSchema = z
    .string()
    .nonempty()
    .max(200)
    .regex(/^[A-Za-z0-9]+(?:,[A-Za-z0-9]+)*$/)
    .transform(normalizeVariationIds);

export const tileListQueryParamSchema = z.object({
    rule: ruleSchema,
    o: offsetSchema.optional(),
    sortiertnach: sortOrderSchema.optional(),
    l: layoutSchema,
    c: compareIdSchema.optional(),
    st: searchTermSchema.optional(),
    variationIds: variationIdsSchema.optional(),
});

export const limitSchema = z.string().regex(/^\d+$/);

export const productsQueryParamSchema = z.object({
    rule: ruleSchema,
    'ranked.offset': offsetSchema.optional(),
    'ranked.limit': limitSchema.optional(),
    'ranked.sortOrder': sortOrderSchema.optional(),
});

export const fallbackQueryParamSchema = z.object({
    rule: ruleSchema,
});

export type TileListQueryParams = z.infer<typeof tileListQueryParamSchema>;
export type FallbackQueryParams = z.infer<typeof fallbackQueryParamSchema>;
